import React from 'react';
import { ClockIcon } from '../../../../../ui/icons/icons';
import { convertSecondsToTimeTaken } from '../../../../../utils/helpers/time';
import {
  AssessmentQuestionInfo,
  UserAssessment,
  UserAssessmentResponse,
} from '../../../../../utils/types/assessment_types';
import { QuestionArea } from './question_area/question_area';

type QuestionProps = {
  selectedAssessment: UserAssessment | undefined;
  videoElementRef: React.RefObject<HTMLVideoElement>;
  currentQuestion: AssessmentQuestionInfo | undefined;
  currentAnswer: string | File | undefined;
  setCurrentAnswer: (value: string | File | undefined) => void;
  nextQuestion: (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => Promise<void>;
  recordedVideo: string | undefined;
  setRecordedVideo: (value: string | undefined) => void;
  timeRemaining: number | undefined;
  isPractice: boolean | undefined;
  questionNumber: number | undefined;
  recordedAudio?: string | undefined;
  questionCount: number;
  loading: boolean;
  progress: number;
  uploadStatus: 'initial' | 'loading' | 'success' | 'error';
  setUploadStatus: (
    value:
      | ((
          prevState: 'initial' | 'loading' | 'success' | 'error',
        ) => 'initial' | 'loading' | 'success' | 'error')
      | 'initial'
      | 'loading'
      | 'success'
      | 'error',
  ) => void;
  handleQuestionSubmission: (
    video: File,
    timeExpired: boolean,
  ) => Promise<void>;
  responseUserAssessment: UserAssessmentResponse | undefined;
  handleNavigationResult: (navResponse: any) => void;
  setQuestionCount: (value: ((prevState: number) => number) | number) => void;
  submitNoFile: (timeExpired: boolean) => Promise<void>;
  setRecordedAudio: (
    value:
      | ((prevState: string | undefined) => string | undefined)
      | string
      | undefined,
  ) => void;
};

export const Question = ({
  selectedAssessment,
  nextQuestion,
  currentQuestion,
  currentAnswer,
  setCurrentAnswer,
  recordedVideo,
  setRecordedVideo,
  timeRemaining,
  isPractice,
  questionNumber,
  recordedAudio,
  questionCount,
  loading,
  progress,
  uploadStatus,
  setUploadStatus,
  handleQuestionSubmission,
  responseUserAssessment,
  handleNavigationResult,
  setQuestionCount,
  submitNoFile,
  setRecordedAudio,
}: QuestionProps): JSX.Element => {
  const totalQuestions = selectedAssessment?.assessment?.totalScore ?? 8;
  const totalTimeInSeconds = selectedAssessment?.assessment?.totalTime ?? 2700;
  const isBehavioral = currentQuestion?.questionType === 'Behavioral';

  // Format time display: if less than 60 seconds, show seconds, otherwise show minutes
  const formatTimeDisplay = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} seconds`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${
      minutes !== 1 ? 's' : ''
    } and ${remainingSeconds} seconds`;
  };

  // Stable display states to prevent unmounting/flickering during fetches
  const [displayQuestion, setDisplayQuestion] = React.useState(currentQuestion);
  const [displayNumber, setDisplayNumber] = React.useState(questionNumber);
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    // When a new question is ready, or current question is cleared (loading), run transition
    if (currentQuestion && currentQuestion.id !== displayQuestion?.id) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setDisplayQuestion(currentQuestion);
        setDisplayNumber(questionNumber);
        // Reset response states for the new question during the fade-out
        setRecordedVideo(undefined);
        setRecordedAudio(undefined);
        setCurrentAnswer(undefined);
        setIsVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    }
    // If no question initially, but one arrives
    if (currentQuestion && !displayQuestion) {
      setDisplayQuestion(currentQuestion);
      setDisplayNumber(questionNumber);
    }
    // If we transition to a loading state (no current question), fade out
    if (!currentQuestion && isVisible) {
      setIsVisible(false);
    }
  }, [currentQuestion?.id, displayQuestion?.id, questionNumber]);

  const totalTimeDisplay = formatTimeDisplay(totalTimeInSeconds);

  return (
    <div className='h-full w-full max-w-7xl mx-auto bg-gray-50 p-2 md:p-4 overflow-hidden'>
      <div className='h-full mx-auto grid grid-cols-1 gap-3 md:gap-4'>
        { /* Main Content - Questions Area */ }
        <div
            className={`flex flex-col gap-2 md:gap-3 h-full overflow-y-auto pr-1 custom-scrollbar transition-opacity duration-300 ease-in-out ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
        >
          { /* Question Header */ }
          <div className='bg-white shadow-sm rounded-xl md:rounded-2xl p-2 '>
            <div className='flex items-center justify-between'>
              <div className='flex gap-1.5 md:gap-3 flex-wrap'>
                <span className='bg-blue-100 text-blue-700 text-xs md:text-sm font-medium px-2 py-1 md:px-4 md:py-2 rounded-full flex items-center gap-1 md:gap-2'>
                  Question { displayNumber }
                </span>
                <span className='bg-green-100 text-green-700 text-xs md:text-sm font-medium px-2 py-1 md:px-4 md:py-2 rounded-full'>
                  { ['Classic', 'Modern'].includes(
                    displayQuestion?.questionType ?? '',
                  )
                    ? 'Skill'
                    : displayQuestion?.questionType || 'Video Response' }
                </span>
              </div>
            </div>
          </div>

          { /* Question Content */ }
          <div className='bg-white shadow-sm rounded-xl md:rounded-2xl md:overflow-hidden p-2'>
            { displayQuestion?.attribute && (
              <div className='border-b border-gray-100 p-2'>
                <p className='text-base font-medium text-gray-700 text-center'>
                  These are questions pertaining to your{ ' ' }
                  <span className='font-semibold text-emerald-700 inline-block '>
                    { displayQuestion.attribute }
                  </span>{ ' ' }
                  skills
                </p>
              </div>
            ) }
            <div className='border-b border-gray-100 p-2 '>
              <p className='text-sm md:text-lg font-medium text-gray-900'>
                { uploadStatus === 'loading' ? (
                  <>
                    <span className='inline-flex items-center text-gray-500 animate-pulse'>
                      Please wait while the system processes your previous
                      answer
                      <span className='inline-flex gap-1 ml-1'>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </span>
                    </span>
                  </>
                ) : (
                  displayQuestion?.question
                ) }
              </p>
            </div>

            <div className='p-2 md:p-4'>
              { displayQuestion && (
                <QuestionArea
                    currentAnswer={currentAnswer}
                    questionType={displayQuestion.questionType || 'Classic'}
                    setCurrentAnswer={setCurrentAnswer}
                    recordedVideo={recordedVideo}
                    setRecordedVideo={setRecordedVideo}
                    currentQuestion={displayQuestion}
                    timeRemaining={timeRemaining}
                    setRecordedAudio={setRecordedAudio}
                    submissionDisabled={currentAnswer === undefined}
                    submitAnswer={nextQuestion}
                    loading={loading}
                    uploadStatus={uploadStatus}
                    setUploadStatus={setUploadStatus}
                    handleQuestionSubmission={handleQuestionSubmission}
                    responseUserAssessment={responseUserAssessment}
                    handleNavigationResult={handleNavigationResult}
                    questionCount={questionCount}
                    setQuestionCount={setQuestionCount}
                    submitNoFile={submitNoFile}
                    totalQuestions={totalQuestions}
                    isBehavioral={isBehavioral}
                />
              ) }
            </div>
            { uploadStatus === 'loading' && (
              <div className='relative my-5 w-full h-4 rounded-md bg-gray-200 overflow-hidden'>
                { /* Progress fill */ }
                <div
                    className='h-full bg-primary-gradient transition-all duration-200'
                    style={{ width: `${Math.floor(progress)}%` }}
                />

                { /* Centered text over entire bar */ }
                <span className='absolute inset-0 flex items-center justify-center text-xs font-medium text-white pointer-events-none'>
                  Uploading Answer { Math.floor(progress) }%
                </span>
              </div>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
};
